import { memo } from 'react';
import { Focusable } from 'react-aria-components';

import { merge } from '@/components';
import { Flex, Text, Tooltip, TooltipTrigger } from '@/components/core';
import { DivProps, OmitChildren } from '@/utils';

import { useValueVariant } from './useValueVariant';

export type BadgeCellProps = OmitChildren<DivProps> & {
  value: number;
  lowerIsBetter?: boolean;
  tooltip?: string;
  isEmpty?: boolean;
};

const IBadgeCell = ({ value, lowerIsBetter, tooltip, isEmpty, className, ...rest }: BadgeCellProps) => {
  const valueVariant = useValueVariant(value, lowerIsBetter);

  return (
    <>
      {isEmpty ? (
        <Text size="xs">—</Text>
      ) : (
        <TooltipTrigger>
          <Tooltip className="w-48" variant="primary">
            <Text slot="description">{tooltip}</Text>
          </Tooltip>

          <Focusable>
            <Flex
              className={merge('w-fit cursor-default items-center gap-1 rounded-sm px-1.5 shadow-xs', className)}
              variant="secondary"
              role="button"
              {...rest}
            >
              <div
                className={merge(
                  'bg-primary-500 h-1 w-1 rounded-full',
                  valueVariant === 'success' && 'bg-success-500',
                  valueVariant === 'warning' && 'bg-warning-500',
                  valueVariant === 'secondary' && 'bg-gray-500'
                )}
              />
              <Text size="xs">{(value * 100).toFixed(1)}%</Text>
            </Flex>
          </Focusable>
        </TooltipTrigger>
      )}
    </>
  );
};

export const BadgeCell = memo(IBadgeCell) as typeof IBadgeCell;
